import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import { FormField, TextInput } from './QuoteFormField';

// ─── Button-group selector ────────────────────────────────────────────────────

function ButtonGroup({ options, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all duration-150 ${
            value === opt.value
              ? 'border-[#1e3a5f] bg-[#1e3a5f] text-white shadow-sm'
              : 'border-gray-200 bg-white text-gray-700 hover:border-[#1e3a5f] hover:text-[#1e3a5f]'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Per-coverage field definitions ──────────────────────────────────────────

const FIELD_CONFIG = {
  'Personal Auto': {
    dbType: 'auto',
    fields: [
      {
        key: 'vehicle_count',
        label: 'Number of Vehicles',
        factorName: 'vehicle_count',
        options: [
          { value: '1_2',    label: '1–2 vehicles' },
          { value: '3_plus', label: '3+ vehicles' },
        ],
      },
      {
        key: 'driver_age',
        label: 'Primary Driver Age Bracket',
        factorName: 'driver_age',
        options: [
          { value: 'under_25', label: 'Under 25' },
          { value: '25_65',    label: '25–65' },
          { value: 'over_65',  label: 'Over 65' },
        ],
      },
    ],
  },
  'Homeowners': {
    dbType: 'home',
    fields: [
      {
        key: 'year_built',
        label: 'Year Home Was Built',
        factorName: 'year_built',
        options: [
          { value: 'pre_1980',  label: 'Before 1980' },
          { value: '1980_2010', label: '1980–2010' },
          { value: '2010_plus', label: '2010 or newer' },
        ],
      },
      {
        key: 'dwelling_value',
        label: 'Estimated Dwelling Value',
        factorName: 'dwelling_value',
        options: [
          { value: 'under_400k', label: 'Under $400k' },
          { value: '400k_800k',  label: '$400k–$800k' },
          { value: 'over_800k',  label: 'Over $800k' },
        ],
      },
    ],
  },
  'Renters': {
    dbType: null, // no rate table rows yet — shows fields but falls back gracefully
    fields: [
      {
        key: 'resident_count',
        label: 'Number of Residents',
        factorName: 'resident_count',
        options: [
          { value: '1',    label: '1' },
          { value: '2_3',  label: '2–3' },
          { value: '4_plus', label: '4+' },
        ],
      },
      {
        key: 'property_value',
        label: 'Estimated Personal Property Value',
        factorName: 'property_value',
        options: [
          { value: 'under_10k', label: 'Under $10k' },
          { value: '10k_30k',   label: '$10k–$30k' },
          { value: 'over_30k',  label: 'Over $30k' },
        ],
      },
    ],
  },
  'Commercial Auto': {
    dbType: null,
    fields: [
      {
        key: 'commercial_vehicle_count',
        label: 'Number of Commercial Vehicles',
        factorName: 'commercial_vehicle_count',
        options: [
          { value: '1',     label: '1' },
          { value: '2_5',   label: '2–5' },
          { value: '6_10',  label: '6–10' },
          { value: '11_plus', label: '11+' },
        ],
      },
      {
        key: 'vehicle_use',
        label: 'Primary Vehicle Use',
        factorName: 'vehicle_use',
        options: [
          { value: 'delivery',       label: 'Delivery' },
          { value: 'transportation', label: 'Transportation' },
          { value: 'construction',   label: 'Construction' },
          { value: 'service',        label: 'Service' },
          { value: 'other',          label: 'Other' },
        ],
      },
    ],
  },
};

// Coverage types that have no estimate step at all — wizard skips straight to step 3
export const AGENT_ESTIMATE_TYPES = [
  'Business Owners Policy',
  'Commercial General Liability',
  'Workers Compensation',
  'Umbrella',
];

// ─── Calculation ──────────────────────────────────────────────────────────────

async function calculateEstimate(dbType, factorInputs, zip) {
  if (!dbType) return null;

  const { data: factors, error } = await supabase
    .from('rate_estimate_factors')
    .select('factor_name, factor_value, multiplier, base_rate')
    .eq('coverage_type', dbType);

  if (error || !factors?.length) return null;

  const baseRow = factors.find((r) => r.factor_name === 'base');
  if (!baseRow?.base_rate) return null;
  const base = Number(baseRow.base_rate);

  let zipMultiplier = 1.0;
  if (zip && /^\d{5}$/.test(zip)) {
    const { data: zipRow } = await supabase
      .from('rate_estimate_zip_tiers')
      .select('multiplier')
      .eq('zip_code', zip.trim())
      .maybeSingle();
    if (zipRow?.multiplier) zipMultiplier = Number(zipRow.multiplier);
  }

  let productMultiplier = 1.0;
  for (const { factorName, value } of factorInputs) {
    const row = factors.find((r) => r.factor_name === factorName && r.factor_value === value);
    if (row) productMultiplier *= Number(row.multiplier);
  }

  const midpoint = base * zipMultiplier * productMultiplier;
  const low  = Math.round(midpoint * 0.9 / 10) * 10;
  const high = Math.round(midpoint * 1.1 / 10) * 10;

  return { low, high };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function QuoteStepEstimate({ form, onChange, onNext, onBack }) {
  const coverageType = form.coverage_type || 'Personal Auto';
  const config = FIELD_CONFIG[coverageType];

  // Unknown coverage type: default to Personal Auto fields with a console warning
  if (!config) {
    console.warn(`QuoteStepEstimate: unknown coverage_type "${coverageType}", defaulting to Personal Auto`);
  }
  const activeConfig = config || FIELD_CONFIG['Personal Auto'];

  // Dynamic selections — keyed by field.key
  const [selections, setSelections] = useState(() => {
    const init = {};
    for (const f of activeConfig.fields) {
      init[f.key] = form[`estimate_${f.key}`] || '';
    }
    return init;
  });

  const [zip, setZip]         = useState(form.estimate_zip || '');
  const [zipError, setZipError] = useState('');
  const [result, setResult]   = useState(
    form.estimate_low ? { low: form.estimate_low, high: form.estimate_high } : null
  );
  const [loading, setLoading] = useState(false);

  // Sync pre-filled ZIP from form prop (carried from Step 1) — not just on mount,
  // but whenever the prop changes, so the button enables without typing in the field
  useEffect(() => {
    if (form.estimate_zip && form.estimate_zip !== zip) {
      setZip(form.estimate_zip);
    }
  }, [form.estimate_zip]);

  // Validate pre-filled ZIP on mount so invalid values show an error immediately
  useEffect(() => {
    if (zip && !/^\d{5}$/.test(zip)) {
      setZipError('Please enter a valid 5-digit ZIP code.');
    }
  }, []);

  const allSelected = activeConfig.fields.every((f) => selections[f.key]);
  const canCalculate = /^\d{5}$/.test(zip) && allSelected;

  const setSelection = (key, value) => {
    setSelections((prev) => ({ ...prev, [key]: value }));
  };

  const handleCalculate = async () => {
    if (!/^\d{5}$/.test(zip)) {
      setZipError('Please enter a valid 5-digit ZIP code.');
      return;
    }
    setZipError('');
    setLoading(true);

    const factorInputs = activeConfig.fields.map((f) => ({
      factorName: f.factorName,
      value: selections[f.key],
    }));

    try {
      const estimate = await calculateEstimate(activeConfig.dbType, factorInputs, zip);

      // Persist selections into form regardless of whether we got an estimate
      const estimateUpdates = { estimate_zip: zip };
      for (const f of activeConfig.fields) {
        estimateUpdates[`estimate_${f.key}`] = selections[f.key];
      }

      if (estimate) {
        estimateUpdates.estimate_low = estimate.low;
        estimateUpdates.estimate_high = estimate.high;
        estimateUpdates.estimate_factors = { coverage_type: coverageType, zip, ...Object.fromEntries(activeConfig.fields.map((f) => [f.key, selections[f.key]])) };
        onChange(estimateUpdates);
        setResult(estimate);
      } else {
        // Rate table missing or no dbType — skip gracefully
        onChange({ ...estimateUpdates, estimate_low: null, estimate_high: null, estimate_factors: estimateUpdates });
        onNext();
      }
    } catch {
      onNext();
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onChange({ estimate_low: null, estimate_high: null, estimate_factors: null });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">Get a Quick Estimate</h2>
        <p className="text-gray-500">
          Answer a few quick questions to see a ballpark price range before you continue.
        </p>
      </div>

      {/* Inputs card */}
      {!result && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5 shadow-sm">
          {/* ZIP */}
          <FormField label="Your ZIP Code" required error={zipError}>
            <TextInput
              value={zip}
              onChange={(val) => {
                setZip(val);
                if (zipError) setZipError('');
              }}
              placeholder="90703"
              maxLength={5}
            />
          </FormField>

          {/* Dynamic fields from config */}
          {activeConfig.fields.map((f) => (
            <div key={f.key}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {f.label} <span className="text-red-500">*</span>
              </label>
              <ButtonGroup
                options={f.options}
                value={selections[f.key]}
                onChange={(val) => setSelection(f.key, val)}
              />
            </div>
          ))}

          <div className="pt-2 space-y-3">
            <button
              onClick={handleCalculate}
              disabled={!canCalculate || loading}
              className="w-full bg-[#1e3a5f] hover:bg-[#162d4a] disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Calculating...
                </>
              ) : (
                'Calculate Estimate'
              )}
            </button>
            <button
              onClick={handleSkip}
              className="w-full text-gray-400 hover:text-gray-600 text-sm font-medium py-2 transition-colors"
            >
              Skip — take me straight to the quote form
            </button>
            {onBack && (
              <button
                onClick={onBack}
                className="w-full text-gray-500 hover:text-gray-700 text-sm font-medium py-2 transition-colors"
              >
                ← Back
              </button>
            )}
          </div>
        </div>
      )}

      {/* Estimate result card */}
      {result && (
        <div className="space-y-4">
          <div className="bg-[#1e3a5f] text-white rounded-xl p-6 text-center shadow-md">
            <p className="text-sm text-blue-200 mb-1 font-medium uppercase tracking-wide">Estimated Annual Range</p>
            <p className="text-4xl font-bold mb-1">
              ${result.low.toLocaleString()}–${result.high.toLocaleString()}
              <span className="text-lg font-normal text-blue-200">/year</span>
            </p>
            <p className="text-sm text-blue-100 mt-1">
              ≈ ${Math.round(((result.low + result.high) / 2) / 12).toLocaleString()}/month
            </p>
          </div>

          {/* Non-dismissible disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-xs text-amber-800 leading-relaxed">
              <strong>Important:</strong> This is a non-binding estimate based on general rating factors only.
              Your actual premium is determined by a licensed agent and the insurance carrier's underwriting
              guidelines, and may differ significantly based on your full application, credit, claims history,
              and other factors. This estimate does not constitute a quote or offer of insurance.
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={onNext}
              className="w-full bg-[#1e3a5f] hover:bg-[#162d4a] text-white font-semibold py-3.5 rounded-xl transition-colors text-base"
            >
              Continue to Get My Exact Quote →
            </button>
            <button
              onClick={() => setResult(null)}
              className="w-full text-gray-400 hover:text-gray-600 text-sm font-medium py-2 transition-colors"
            >
              ← Adjust my answers
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
